/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import wizardStyle from "@/src/components/Form/wizardStyle";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import AlimonyPerson from "./Alimony/AlimonyPerson";
import { useForm } from "@/src/provider/Form/FormProvider";
import { Box, MenuItem } from "@mui/material";
import Group from "@/src/components/Wizard/Group";
import { oneOfTheseOptionsValidator, requiredValidator } from "@/src/services/validationRules";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import _cloneDeep from "lodash/cloneDeep";
import _slice from "lodash/slice";
import { addMessage } from "@/src/store/messages/actions";
import _toNumber from "lodash/toNumber";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { Person } from "@/src/types/Entities/Person";

type AlimonyProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function Alimony({ product, submitProps }: AlimonyProps) {
  const { values, handleChange, handleSubmit } = useForm();

  useEffect(() => {
    const numberOfAlimonyPersons = values.numberOfAlimonyPersons ? _toNumber(values.numberOfAlimonyPersons) : 0;
    if (values.alimonyPersons.length === numberOfAlimonyPersons) {
      return;
    }
    const emptyPerson = {
      gender: "",
      givenName: "",
      familyName: "",
      livesWithPetitioner: false,
      address: {
        streetAddress: "",
        postalCode: "",
        addressLocality: "",
      },
      birthDate: "",
      relation: "",
      alimony: "",
      hasIncome: "",
      income: "",
    };
    let alimonyPersons = _cloneDeep(values.alimonyPersons);
    for (let i = alimonyPersons.length; i < Math.min(numberOfAlimonyPersons, 5); i++) {
      alimonyPersons.push(_cloneDeep(emptyPerson));
    }
    alimonyPersons = _slice(alimonyPersons, 0, numberOfAlimonyPersons);
    handleChange({
      target: {
        type: "other",
        name: "alimonyPersons",
        value: alimonyPersons,
      },
    });
  }, [values.alimonyPersons, values.numberOfAlimonyPersons]);

  useEffect(() => {
    if (values.hasRelativesAlimonyClaims === "no" && product.divorce.numberChildren > 0) {
      addMessage({
        type: "warning",
        text: "Sie haben zuvor angegeben, dass Sie minderjährige Kinder haben. Wenn Sie Ihren Kindern Unterhalt zahlen, oder diese bei Ihnen leben und sie sie versorgen, geben Sie das hier unbedingt an. Die Wahrscheinlichkeit, dass Sie Verfahrenskostenhilfe erhalten, ist dann deutlich höher! ",
      });
    }
  }, [values.hasRelativesAlimonyClaims]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Persönliche Angaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasRelativesAlimonyClaims"}
                question={"Müssen Sie an jemanden Unterhalt zahlen, oder leben Kinder bei Ihnen, die Sie versorgen?"}
                modalContent={
                  <p>
                    Bitte geben Sie an, wenn <strong>Sie</strong> jemandem Unterhalt zahlen oder Kinder bei Ihnen leben und durch Sie versorgt werden. Es ist
                    dabei nicht wichtig, ob Sie einen monatlichen Geldbetrag zahlen oder den Unterhalt dadurch leisten, dass diese Person bei Ihnen wohnt und
                    von Ihnen versorgt wird. In beiden Fällen ist die Angabe sehr wichtig, da es die Chance auf Verfahrenskostenhilfe erhöht.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasRelativesAlimonyClaims === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie vielen Personen zahlen Sie Unterhalt?"
                  name="numberOfAlimonyPersons"
                  validators={[oneOfTheseOptionsValidator(["1", "2", "3", "4", "5"], "Bitte geben Sie an wie vielen Personen Sie unterhalt zahlen")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.alimonyPersons?.map((alimonyPerson: Person, index: number) => (
                  <AlimonyPerson key={index} index={index} path={`alimonyPersons[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
